import React, { useRef, useEffect } from 'react';
import { Box } from '@mui/material';
import { AutocompletePaginationAsync } from 'components/Inputs';
import { useEditableAutocomplete } from './useEditableAutocomplete';

interface EditableAutocompleteProps<TOption, TParams> {
  value: any;
  onChange: (value: any) => void;
  onSave: (value: any) => void;
  onCancel: () => void;
  useApiHook: () => any;
  getterName: string;
  defaultParams: TParams;
  getOptionLabel: (option: TOption | string) => string;
  isOptionEqualToValue: (option: TOption, selectedValue: any) => boolean;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: TOption,
    state: { selected: boolean },
  ) => React.ReactNode;
  width?: number;
  disableClearable?: boolean;
  disabled?: boolean;
  filterOptions?: (options: TOption[]) => TOption[];
  mapOptions?: (options: TOption[]) => TOption[];
  deduplicateOptions?: boolean;
  deduplicateKey?: keyof TOption;
  additionalDependency?: any;
  skipFetchCondition?: () => boolean;
  hasValidationError?: boolean;
}

export function EditableAutocomplete<TOption extends { id: any }, TParams>({
  value,
  onChange,
  onSave,
  onCancel,
  useApiHook,
  getterName,
  defaultParams,
  getOptionLabel,
  isOptionEqualToValue,
  width,
  renderOption,
  disableClearable = false,
  disabled = false,
  filterOptions,
  mapOptions,
  deduplicateOptions = true,
  deduplicateKey = 'id' as keyof TOption,
  additionalDependency,
  skipFetchCondition,
  hasValidationError = false,
}: EditableAutocompleteProps<TOption, TParams>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const latestValueRef = useRef<any>(value);
  const hasSavedRef = useRef<boolean>(false);

  useEffect(() => {
    latestValueRef.current = value;
  }, [value]);

  const {
    options,
    loading,
    handleSearchChange,
    handleChange,
    handleScrollBottom,
  } = useEditableAutocomplete<TOption, TParams>({
    value,
    onChange: (newValue) => {
      latestValueRef.current = newValue;
      onChange(newValue);
    },
    onSave: (newValue) => {
      hasSavedRef.current = true;
      onSave(newValue);
    },
    useApiHook,
    getterName,
    defaultParams,
    filterOptions,
    mapOptions,
    deduplicateOptions,
    deduplicateKey,
    additionalDependency,
    skipFetchCondition,
  });

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onCancel();
    } else if (event.key === 'Enter') {
      onSave(value);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (hasSavedRef.current) {
        hasSavedRef.current = false;
        return;
      }

      const currentFocus = document.activeElement;

      if (
        !containerRef.current ||
        !containerRef.current.contains(currentFocus)
      ) {
        onSave(latestValueRef.current);
      }
    }, 200);
  };

  useEffect(() => {
    const handleClickOutside = (_event: MouseEvent) => {};

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={containerRef} onKeyDown={handleKeyDown} style={{ width: '100%' }}>
      <Box
        sx={{
          minWidth: 'auto',
          width: '100%',
          '& .MuiOutlinedInput-root': {
            height: '24px',
            minHeight: '24px',
            padding: '0px 8px',
            fontSize: '0.7rem',
          },
          '& .MuiInputBase-input': {
            padding: '0px',
            height: '24px',
            lineHeight: '1.5em',
            fontSize: '0.7rem',
          },
          '& .MuiAutocomplete-endAdornment': {
            top: 'calc(50% - 10px)',
            ...(hasValidationError && { right: '26px !important' }),
          },
          '& .MuiAutocomplete-clearIndicator': {
            padding: '1px',
            '& .MuiSvgIcon-root': {
              fontSize: '14px',
            },
          },
          '& .MuiAutocomplete-popupIndicator': {
            padding: '1px',
            '& .MuiSvgIcon-root': {
              fontSize: '14px',
            },
          },
          '& .MuiCircularProgress-root': {
            width: '12px !important',
            height: '12px !important',
          },
        }}
      >
        <AutocompletePaginationAsync
          padding='dense'
          width={width}
          value={value}
          loading={loading}
          disabled={disabled}
          options={options}
          disableClearable={disableClearable}
          isOptionEqualToValue={isOptionEqualToValue}
          getOptionLabel={getOptionLabel}
          renderOption={renderOption}
          handleSearchChange={handleSearchChange}
          onChange={handleChange}
          onScrollBottom={handleScrollBottom}
          onBlur={handleBlur}
          {...({
            componentsProps: {
              paper: {
                sx: {
                  '& .MuiAutocomplete-option': {
                    fontSize: '0.7rem',
                    padding: '2px 8px',
                    minHeight: '28px',
                  },
                  '& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading': {
                    fontSize: '0.7rem',
                    padding: '4px 8px',
                  },
                },
              },
            },
          } as any)}
        />
      </Box>
    </div>
  );
}
